<template>
  <div :class="['duration', classes]">
    <v-menu
      offset-y
      eager
      v-bind="menuProps"
      :disabled="disabled"
      @input="open"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          readonly
          :value="inputValue"
          :error-messages="messagesToDisplay"
          :disabled="disabled"
          v-bind="{
            ...$attrs,
            ...attrs
          }"
          v-on="on"
        />
      </template>

      <v-sheet class="duration__wrapper">
        <duration-item
          v-if="!hideDays"
          :value="parsedValue.days"
          type="d"
          :multiplier="$options.SECONDS_DAY"
          :allowed-values="allowedDays"
          :options="daysOptions"
          :color="color"
          :hide-not-allowed-options="hideNotAllowedOptions"
          @input="updateValue"
        />

        <duration-item
          v-if="!hideHours"
          :value="parsedValue.hours"
          type="h"
          :multiplier="$options.SECONDS_HOUR"
          :allowed-values="allowedHours"
          :options="hoursOptions"
          :color="color"
          :hide-not-allowed-options="hideNotAllowedOptions"
          @input="updateValue"
        />

        <duration-item
          v-if="!hideMinutes"
          :value="parsedValue.minutes"
          type="m"
          :multiplier="$options.SECONDS_MINUTE"
          :allowed-values="allowedMinutes"
          :options="minutesOptions"
          :color="color"
          :hide-not-allowed-options="hideNotAllowedOptions"
          @input="updateValue"
        />

        <duration-item
          v-if="!hideSeconds"
          :value="parsedValue.seconds"
          type="s"
          :allowed-values="allowedSeconds"
          :options="secondsOptions"
          :color="color"
          :hide-not-allowed-options="hideNotAllowedOptions"
          @input="updateValue"
        />
      </v-sheet>
    </v-menu>
  </div>
</template>

<script>
import themeable from 'vuetify/lib/mixins/themeable';
import localable from 'vuetify/lib/mixins/localable';
import validatable from 'vuetify/lib/mixins/validatable';
import DurationItem from './DurationItem.vue';

const SECONDS_DAY = 86400;
const SECONDS_HOUR = 3600;
const SECONDS_MINUTE = 60;

export default {
  name: 'Duration',

  SECONDS_DAY,
  SECONDS_HOUR,
  SECONDS_MINUTE,

  components: {
    DurationItem
  },

  provide() {
    return {
      register: this.addComponent,
      unregister: this.removeComponent
    };
  },

  mixins: [themeable, localable, validatable],

  props: {
    value: {
      type: Number,
      required: true
    },
    color: {
      type: String,
      default: 'primary'
    },
    menuProps: {
      type: [String, Array, Object],
      default: () => ({
        closeOnClick: true,
        closeOnContentClick: false,
        disableKeys: true,
        openOnClick: true,
        maxHeight: 300,
        contentClass: 'duration__content'
      })
    },
    allowedSeconds: {
      type: Function,
      default: () => true
    },
    allowedMinutes: {
      type: Function,
      default: () => true
    },
    allowedHours: {
      type: Function,
      default: () => true
    },
    allowedDays: {
      type: Function,
      default: () => true
    },
    hideSeconds: {
      type: Boolean,
      default: false
    },
    hideMinutes: {
      type: Boolean,
      default: false
    },
    hideHours: {
      type: Boolean,
      default: false
    },
    hideDays: {
      type: Boolean,
      default: false
    },
    hideNotAllowedOptions: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      components: [],
      daysOptions: [...Array(31).keys()],
      hoursOptions: [...Array(24).keys()],
      minutesOptions: [...Array(60).keys()],
      secondsOptions: [...Array(60).keys()]
    };
  },

  computed: {
    classes() {
      return {
        ...this.themeClasses
      };
    },

    parsedValue() {
      const parsedValue = parseInt(this.lazyValue);

      if (isNaN(parsedValue) || !isFinite(parsedValue)) {
        return;
      }

      let days = (parsedValue / SECONDS_DAY) | 0;
      let hours = ((parsedValue % SECONDS_DAY) / SECONDS_HOUR) | 0;
      let minutes = ((parsedValue % SECONDS_HOUR) / SECONDS_MINUTE) | 0;
      let seconds = (parsedValue % SECONDS_HOUR) % SECONDS_MINUTE | 0;

      return { days, hours, minutes, seconds };
    },

    selectedSeconds() {
      return this.components.reduce((v, c) => v + c.getSeconds(), 0);
    },

    inputValue() {
      return this.components.map(c => c.getText()).join(' ');
    },

    messagesToDisplay() {
      if (!this.hasMessages) return [];
      return this.validations
        .map(validation => {
          if (typeof validation === 'string') return validation;
          const validationResult = validation(this.internalValue);
          return typeof validationResult === 'string' ? validationResult : '';
        })
        .filter(message => message !== '');
    }
  },

  methods: {
    open() {
      this.$nextTick(() => {
        this.components.forEach(c => {
          setTimeout(c.scroll, 100);
        });
      });
    },
    updateValue() {
      this.internalValue = this.selectedSeconds;
    },

    addComponent(component) {
      this.components.push(component);
    },

    removeComponent(component) {
      this.components = this.components.filter(i => i._uid !== component._uid);
    }
  }
};
</script>

<style lang="scss" scoped>
.duration {
  &__content {
    display: flex;
  }

  &__wrapper {
    display: flex;
    flex: 1;
    overflow: auto;
  }
}
</style>
